import React, { createContext, useState, useEffect, useContext, useMemo } from "react";
import {
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
  getLanguageByCode,
} from "../i18n/languages";

// Import translation bundles
import en from "../i18n/locales/en.json";
import ro from "../i18n/locales/ro.json";
import de from "../i18n/locales/de.json";
import bg from "../i18n/locales/bg.json";
import hu from "../i18n/locales/hu.json";
import te from "../i18n/locales/te.json";
import hi from "../i18n/locales/hi.json";

const TRANSLATIONS = {
  en,
  ro,
  de,
  bg,
  hu,
  te,
  hi,
};

const LOCAL_STORAGE_KEY = "tvz_language";

export const LanguageContext = createContext();

function getInitialLanguage() {
  try {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (saved) {
      return getLanguageByCode(saved);
    }

    // Auto-detect browser language if available
    const browserLang = navigator.language || navigator.userLanguage;
    if (browserLang) {
      const code = browserLang.toLowerCase().split("-")[0];
      const match = SUPPORTED_LANGUAGES.find((l) => l.code === code);
      if (match) return match;
    }
  } catch (e) {
    // LocalStorage or navigator inaccessible
  }

  return getLanguageByCode(DEFAULT_LANGUAGE);
}

export function LanguageProvider({ children }) {
  const [currentLang, setCurrentLang] = useState(getInitialLanguage);

  const changeLanguage = (code) => {
    const newLang = getLanguageByCode(code);
    setCurrentLang(newLang);
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, newLang.code);
      document.documentElement.lang = newLang.code;
      document.documentElement.dir = newLang.direction || "ltr";
    } catch (e) {}
  };

  useEffect(() => {
    try {
      document.documentElement.lang = currentLang.code;
      document.documentElement.dir = currentLang.direction || "ltr";
    } catch (e) {}
  }, [currentLang]);

  // Translation helper: t('nav.events', 'Events')
  const t = (path, fallbackOrParams, maybeParams) => {
    let fallback = typeof fallbackOrParams === "string" ? fallbackOrParams : "";
    let params = typeof fallbackOrParams === "object" ? fallbackOrParams : maybeParams;

    const resolveKey = (bundle, keyPath) => {
      if (!bundle) return null;
      const keys = keyPath.split(".");
      let current = bundle;
      for (const k of keys) {
        if (current && typeof current === "object" && k in current) {
          current = current[k];
        } else {
          return null;
        }
      }
      return typeof current === "string" ? current : null;
    };

    // 1. Try current language
    let text = resolveKey(TRANSLATIONS[currentLang.code], path);

    // 2. Fall back to English
    if (!text && currentLang.code !== "en") {
      text = resolveKey(TRANSLATIONS["en"], path);
    }

    // 3. Fall back to supplied fallback or path
    if (!text) {
      text = fallback || path;
    }

    // Interpolate {variable} if params provided
    if (params && typeof params === "object") {
      Object.keys(params).forEach((key) => {
        text = text.replace(new RegExp(`{${key}}`, "g"), params[key]);
      });
    }

    return text;
  };

  const contextValue = useMemo(
    () => ({
      currentLanguage: currentLang,
      language: currentLang.code,
      setLanguage: changeLanguage,
      languages: SUPPORTED_LANGUAGES,
      t,
    }),
    [currentLang]
  );

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}

export default LanguageContext;
